import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';

const fmt = n => Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Notices() {
  const [consumers, setConsumers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [groupByBarangay, setGroupByBarangay] = useState(false);
  const [barangayFilter, setBarangayFilter] = useState('');
  const [smsStatus, setSmsStatus] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    api.get('/notices').then(r => { setConsumers(r.data); setLoading(false); });
  }, []);

  const barangays = useMemo(() => {
    const names = [...new Set(consumers.map(c => c.barangay_name))].sort();
    return names;
  }, [consumers]);

  const filtered = useMemo(() => {
    let list = consumers;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q) || c.account_code.toLowerCase().includes(q));
    }
    if (barangayFilter) {
      list = list.filter(c => c.barangay_name === barangayFilter);
    }
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [consumers, search, barangayFilter]);

  const grouped = useMemo(() => {
    if (!groupByBarangay) return null;
    const map = {};
    filtered.forEach(c => {
      if (!map[c.barangay_name]) map[c.barangay_name] = [];
      map[c.barangay_name].push(c);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupByBarangay]);

  const showDetail = async (consumerId) => {
    const r = await api.get(`/notices/${consumerId}`);
    setDetail(r.data);
  };

  const printNotice = () => {
    const w = window.open('', '_blank');
    const d = detail;
    w.document.write(`
      <html><head><title>Collection Notice - ${d.consumer.name}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;max-width:700px;margin:auto}
      h1{font-size:18px;text-align:center}h2{font-size:14px;text-align:center;color:#666}
      table{width:100%;border-collapse:collapse;margin:20px 0}th,td{border:1px solid #ccc;padding:8px;text-align:left}
      th{background:#f5f5f5}.total{font-size:16px;font-weight:bold;text-align:right;margin-top:16px}
      .notice-text{margin:20px 0;line-height:1.6}</style></head><body>
      <h1>LOBOC MUNICIPAL WATERWORKS</h1>
      <h2>COLLECTION NOTICE</h2>
      <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-PH')}</p>
      <p><strong>Account:</strong> ${d.consumer.account_code}<br>
      <strong>Name:</strong> ${d.consumer.name}<br>
      <strong>Address:</strong> ${d.consumer.address || '-'}<br>
      <strong>Barangay:</strong> ${d.consumer.barangay_name}</p>
      <p class="notice-text">Dear ${d.consumer.name},<br><br>
      Our records show that your water account has the following outstanding balance(s).
      Please settle your account on or before the due date to avoid disconnection of water service.</p>
      <table><thead><tr><th>Period</th><th>Due Date</th><th style="text-align:right">Amount</th><th style="text-align:right">Penalty</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${d.bills.map(b => `<tr><td>${b.billing_period}</td><td>${b.due_date}</td>
      <td style="text-align:right">PHP ${Number(b.water_charge).toFixed(2)}</td>
      <td style="text-align:right">PHP ${Number(b.penalty).toFixed(2)}</td>
      <td style="text-align:right">PHP ${Number(b.total_amount - b.payment_amount).toFixed(2)}</td></tr>`).join('')}
      </tbody></table>
      <p class="total">Total Balance: PHP ${Number(d.total_due).toFixed(2)}</p>
      <p style="margin-top:40px">Thank you for your prompt attention to this matter.</p>
      <p style="margin-top:40px">_________________________<br>Authorized Signature</p>
      </body></html>
    `);
    w.document.close();
    w.print();
  };

  const sendSms = async (consumerIds) => {
    setSmsSending(true);
    setSmsStatus('');
    try {
      const r = await api.post('/notices/send-sms', { consumer_ids: consumerIds });
      setSmsStatus(r.data.message);
    } catch (e) {
      setSmsStatus('Error: ' + (e.response?.data?.error || e.message));
    }
    setSmsSending(false);
  };

  const sendSmsToAll = () => {
    if (!window.confirm(`Send SMS overdue notice to ${filtered.length} consumers?`)) return;
    sendSms(filtered.map(c => c.id));
  };

  const sendSmsToOne = (consumerId) => {
    sendSms([consumerId]);
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  const renderTable = (list) => (
    <div className="table-wrap">
      <table>
        <thead><tr><th>Account</th><th>Name</th>{!groupByBarangay && <th>Barangay</th>}<th className="text-right">Unpaid Bills</th><th className="text-right">Total Balance</th><th>Action</th></tr></thead>
        <tbody>
          {list.map(c => (
            <tr key={c.id}>
              <td>{c.account_code}</td>
              <td>{c.name}</td>
              {!groupByBarangay && <td>{c.barangay_name}</td>}
              <td className="text-right">{c.unpaid_count}</td>
              <td className="text-right text-red">PHP {fmt(c.total_due)}</td>
              <td><button className="btn btn-primary btn-sm" onClick={() => showDetail(c.id)}>View</button></td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={groupByBarangay ? 5 : 6} style={{ textAlign: 'center', color: '#666' }}>No matching consumers</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <h1 className="page-title">Collection Notices</h1>

      {detail && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="flex-between">
            <div>
              <h3>{detail.consumer.name}</h3>
              <span style={{ color: '#666' }}>{detail.consumer.account_code} - {detail.consumer.barangay_name}</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" style={{ background: '#42B72A', color: 'white' }} onClick={() => sendSmsToOne(detail.consumer.id)} disabled={smsSending}>
                {smsSending ? 'Sending...' : 'Send SMS'}
              </button>
              <button className="btn btn-primary" onClick={printNotice}>Print Notice</button>
              <button className="btn" style={{ background: '#eee' }} onClick={() => { setDetail(null); setSmsStatus(''); }}>Close</button>
            </div>
          </div>
          {smsStatus && <div style={{ margin: '8px 0', padding: '8px 12px', borderRadius: 6, background: smsStatus.startsWith('Error') ? '#fde' : '#e8f5e9', color: smsStatus.startsWith('Error') ? '#d32f2f' : '#2e7d32', fontSize: 13 }}>{smsStatus}</div>}
          <hr style={{ margin: '12px 0' }} />
          <div className="table-wrap">
            <table>
              <thead><tr><th>Period</th><th>Due Date</th><th className="text-right">Charge</th><th className="text-right">Penalty</th><th className="text-right">Balance</th></tr></thead>
              <tbody>
                {detail.bills.map(b => (
                  <tr key={b.id}>
                    <td>{b.billing_period}</td>
                    <td>{b.due_date}</td>
                    <td className="text-right">PHP {fmt(b.water_charge)}</td>
                    <td className="text-right">PHP {fmt(b.penalty)}</td>
                    <td className="text-right text-red">PHP {fmt(b.total_amount - b.payment_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, fontSize: 18, fontWeight: 'bold' }} className="text-red text-right">
            Total: PHP {fmt(detail.total_due)}
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Delinquent Consumers ({filtered.length})</h3>
          <button className="btn btn-sm" style={{ background: '#42B72A', color: 'white' }} onClick={sendSmsToAll} disabled={smsSending || filtered.length === 0}>
            {smsSending ? 'Sending...' : `Send SMS to All (${filtered.length})`}
          </button>
        </div>
        {smsStatus && !detail && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: smsStatus.startsWith('Error') ? '#fde' : '#e8f5e9', color: smsStatus.startsWith('Error') ? '#d32f2f' : '#2e7d32', fontSize: 13 }}>{smsStatus}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="form-input"
              style={{ width: 220, padding: '6px 12px' }}
            />
            <select
              value={barangayFilter}
              onChange={e => setBarangayFilter(e.target.value)}
              className="form-input"
              style={{ width: 'auto', padding: '6px 12px' }}
            >
              <option value="">All Barangays</option>
              {barangays.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={groupByBarangay} onChange={e => setGroupByBarangay(e.target.checked)} />
              Group by Barangay
            </label>
          </div>

        {groupByBarangay && grouped ? (
          grouped.map(([barangay, list]) => (
            <div key={barangay} style={{ marginBottom: 16 }}>
              <div style={{ padding: '8px 12px', background: '#f0f4f8', borderRadius: 6, fontWeight: 'bold', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{barangay}</span>
                <span style={{ color: '#666', fontWeight: 'normal', fontSize: 13 }}>{list.length} consumer{list.length !== 1 ? 's' : ''} &middot; PHP {fmt(list.reduce((s, c) => s + Number(c.total_due || 0), 0))}</span>
              </div>
              {renderTable(list)}
            </div>
          ))
        ) : (
          renderTable(filtered)
        )}
      </div>
    </div>
  );
}
